<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  Ref,
  ref,
  toRefs,
  UnwrapRef,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/api';
import { PageName } from '@/api/tracking';
import { DgrIcon, DgrLoading } from '@stockmarkteam/donguri-ui';
import AdpDocumentCard from '@/components/common/adp-document-card.vue';
import AdpDocumentInfoDefault from '@/components/common/adp-document/adp-document-info-default.vue';
import AdpDocumentInfo from '@/components/common/adp-document/adp-document-info.vue';
import SearchBar from '@/components/common/molecules/search-bar.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import SwrInfinityScroller from '@/components/common/swr-infinity-scroller.vue';
import Content from '@/components/layouts/content.vue';
import Header from '@/components/layouts/header.vue';
import { AdpDocument } from '@/types';
import { isEmptyObject } from '@/utils';
import { documentKey } from '@/utils/adpDocument';
import { isMypageRestricted as isMypageRestrictedUtil } from '@/utils/mypageAccessScope';
import { selectArticle } from '@/utils/selectArticle';
import {
  STATES,
  useTargetUserInfo,
  useUserMarkedArticleCounts,
  useUserMarkedArticles,
  useUserOrganizationTags,
} from '@/utils/swr';
import { useStore } from '@/utils/vue';

const LIMIT = 10;
const PAGE_NAME: PageName = 'user';

const store = useStore();
const route = useRoute();
const { createSnackbar } = useSnackbar();

interface Props {
  userId?: string | number;
}

const props = defineProps<Props>();
const { userId } = toRefs(props);
const uId = computed(() => (userId?.value ? Number(userId.value) : undefined));

const { data: userOrganizationTags } = useUserOrganizationTags(uId);

const userInfo = computed(() => store.state.userInfo.userInfo);
const { data: targetUserInfo } = useTargetUserInfo(uId);
const isSharing = computed(() => store.state.articleShare.isSharing);
const selectedArticles = ref<AdpDocument[]>([]);

const abortController = new AbortController();
onMounted(async () => {
  await setUp();
});

const setUp = async () => {
  if (uId.value === undefined) {
    return;
  }
  fetchArticles();
  await trackPageView();
};

const updateSearchKeyword = async () => {
  searchQueryForApi.value = searchQuery.value;
};

watch(isSharing, newVal => {
  if (newVal) {
    store.commit('articleShare/setIsSharing', false);
  }
});

onBeforeUnmount(() => {
  abortController.abort();
});

const isMypageRestricted = computed(() => {
  // マイページ公開制限
  if (!targetUserInfo.value) {
    return false;
  }
  if (isEmptyObject(targetUserInfo.value)) {
    return false;
  }

  return isMypageRestrictedUtil(userInfo.value, targetUserInfo.value);
});

const selectedArticle = computed<AdpDocument | undefined>(() => {
  if (selectedArticles.value.length === 1) {
    return selectedArticles.value[0];
  }
  return undefined;
});

const articleSelected = (id: number): boolean | undefined => {
  return selectedArticles.value.map(a => a.id).includes(id);
};

const articleHovered = (id: number): boolean | undefined => {
  return !selectedArticles.value.map(a => a.id).includes(id);
};

const clickArticle = (e: MouseEvent, article: AdpDocument): void => {
  selectedArticles.value = selectArticle(e, article, selectedArticles.value);
};

watch(
  () => uId?.value,
  () => setUp(),
);

const themeId = (article: AdpDocument) => {
  if (article.marks.length === 0) return 0;
  return article.marks[0].theme_id ?? undefined;
};

const trackPageView = async () => {
  const previousRoute = store.state.route.previousRoute;
  const hasPreviousRoute = !!previousRoute?.name;
  const isSnackbar =
    hasPreviousRoute &&
    String(previousRoute.params.userId) !== String(uId.value);
  await api.trackPageView({
    pageName: PAGE_NAME,
    pageUrl: route.fullPath,
    fromUrl: previousRoute?.path ?? null,
    fromSnackbarLink: isSnackbar ? 'article_mark' : undefined,
  });
};

// 検索窓
const searchQuery = ref<string>('');

const searchQueryForApi = ref<string>();

const fetchArticles = () => {
  if (uId.value === undefined) return;
  updateSearchKeyword();
};

const { data: userMarkedArticleCounts, state: userMarkedArticleCountsState } =
  useUserMarkedArticleCounts(uId, searchQueryForApi);

const isFetchingMarkedArticleCounts = computed(
  () => userMarkedArticleCountsState.value !== STATES.SUCCESS,
);

const totalMarkedArticleCounts = computed(() => {
  if (isFetchingMarkedArticleCounts.value) return '-';
  return (
    (userMarkedArticleCounts.value?.counts.article || 0) +
    (userMarkedArticleCounts.value?.counts.patent || 0) +
    (userMarkedArticleCounts.value?.counts.research_paper || 0) +
    (userMarkedArticleCounts.value?.counts.report || 0)
  ).toString();
});

const getMarkedArticles = (
  pageRef: Ref<number>,
  offsetRef: Ref<number>,
  pageLimit: number,
) => {
  return useUserMarkedArticles(
    pageRef,
    offsetRef,
    pageLimit,
    uId,
    searchQueryForApi,
  );
};

const router = useRouter();

const pageNationErrorFunc = (status: number | undefined) => {
  if (status === 404) {
    createSnackbar({
      message: '指定されたメンバーは存在しません',
      type: 'error',
    });
    router.replace('/');
  } else {
    createSnackbar({
      message: '記事の取得に失敗しました',
      type: 'error',
    });
  }
};

const latestMarksDataAccessor = (
  data: UnwrapRef<ReturnType<typeof useUserMarkedArticles>['data']>,
) => data?.marked_articles ?? [];

const pageInfoAccessor = (
  data: UnwrapRef<ReturnType<typeof useUserMarkedArticles>['data']>,
) => {
  return data?.page !== undefined && data?.next !== undefined
    ? {
        page: data.page,
        next: data.next,
      }
    : undefined;
};
</script>

<template>
  <div v-if="targetUserInfo" class="o-my-activity-feed">
    <Header
      :title="targetUserInfo.user_name"
      :avatar-image-url="targetUserInfo.image_url"
      :organization-tag-list="userOrganizationTags?.organization_tags"
    ></Header>
    <Content>
      <template v-if="isMypageRestricted">
        <div class="page-content">
          <div class="restrict-disclosure">
            <DgrIcon size="xlarge" name="lock" />{{ targetUserInfo.user_name }}
            さんはページの公開範囲を制限しています。
          </div>
        </div>
      </template>
      <template v-else>
        <div class="page-content">
          <div>
            <span class="c-title-bold--m"
              >マーク<span class="c-text-regular--s"
                >・{{ totalMarkedArticleCounts }} 件</span
              ></span
            >
            <div class="spacing-24"></div>
            <SearchBar
              class="search-bar"
              :placeholder="'記事を検索'"
              v-model="searchQuery"
              @on-search-query="fetchArticles()"
            ></SearchBar>
            <div class="spacing-24"></div>
          </div>
          <div class="mark-page-content">
            <div class="o-feed">
              <div
                class="o-no-user-marks"
                v-if="totalMarkedArticleCounts === '0'"
              >
                <div class="o-description c-text-regular--m">
                  該当する記事はありませんでした。
                </div>
                <img src="@/assets/state-empty-article.png" />
              </div>
              <SwrInfinityScroller
                :key="`${[uId, searchQueryForApi]}`"
                :page-limit="LIMIT"
                :data-accessor="latestMarksDataAccessor"
                :page-info-accessor="pageInfoAccessor"
                :pagination-func="getMarkedArticles"
                :scroll-target="'window'"
                @error="pageNationErrorFunc"
              >
                <template v-slot="{ items: articles, loading }">
                  <div
                    class="o-article-card"
                    v-for="articleItem in articles"
                    :key="documentKey(articleItem)"
                    @click="clickArticle($event, articleItem)"
                  >
                    <AdpDocumentCard
                      :is-selected="articleSelected(articleItem.id)"
                      :enable-hover="articleHovered(articleItem.id)"
                      :page-name="PAGE_NAME"
                      :adp-document="articleItem"
                      feature="user_marks"
                      :theme-id="themeId(articleItem)"
                      :show-comment="false"
                      :show-tag-list="false"
                    ></AdpDocumentCard>
                  </div>
                  <div class="loading-icon" v-if="loading">
                    <DgrLoading />
                  </div>
                </template>
              </SwrInfinityScroller>
            </div>
            <div class="content-right">
              <div class="content-right-body">
                <AdpDocumentInfo
                  class="article-info"
                  v-if="selectedArticle"
                  :adp-document="selectedArticle"
                  :editable="false"
                  :page-name="PAGE_NAME"
                  :is-group-view="false"
                ></AdpDocumentInfo>
                <AdpDocumentInfoDefault
                  v-else
                  :adp-document="selectedArticles"
                  :page-name="PAGE_NAME"
                >
                </AdpDocumentInfoDefault>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Content>
  </div>
</template>
<style lang="scss" scoped>
.o-my-activity-feed {
  width: 100%;

  .page-content {
    display: flex;
    flex-direction: column;
  }

  .mark-page-content {
    display: grid;
    grid-template-columns: auto auto;
    grid-gap: 16px;
    justify-content: start;
  }

  .c-text-regular--s {
    color: $color-gray600;
  }

  .search-bar {
    width: 616px;
  }

  .action-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 920px;
    height: 32px;
    margin-bottom: 16px;

    .left-box {
      display: flex;
      align-items: center;
    }

    .c-text-regular--s {
      color: $color-gray600;
    }

    .tag-box {
      .tag-selectbox {
        width: auto;
      }
    }
  }

  .selected-tags {
    width: 920px;
  }

  .o-feed {
    margin-top: 0px;
    width: 616px;

    .articles-empty {
      width: 592px;
      height: 54px;
      background: #ffffff;
      border: 1px solid $color-gray400;
      box-sizing: border-box;
      border-radius: 4px;

      .articles-empty-content {
        padding: 16px 0 16px 16px;
      }
    }
  }

  .article-info {
    background: #ffffff;
  }

  .o-no-user-marks {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 616px;
    height: 212px;
    background: #ffffff;
    border: 1px solid $color-gray400;
    box-sizing: border-box;
    border-radius: 4px;
    padding: 0 32px 0 48px;
    img {
      height: 200px;
      width: 200px;
    }
  }
  .o-article-card {
    margin-bottom: 10px;
    cursor: pointer;
  }
  .article-selected {
    border: 1px solid $color-green600;
  }

  .tag-selectbox :deep(.selector-popup) {
    display: none;
  }

  .article-hovered {
    &:hover {
      border-color: $color-gray600;
    }
  }

  .content-right {
    max-height: 80vh;
    overflow-y: auto;
    position: sticky;
    // header(64px) + tab(85px) + 余白(24px)を考慮した数値
    top: 173px;
    width: 292px;
    .content-right-body {
      width: 100%;
    }
  }

  .filters {
    display: flex;
  }
}

.export-button {
  width: 200px;
}

.restrict-disclosure {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 616px;
  height: 212px;
  background: #ffffff;
  border: 1px solid $color-gray400;
  box-sizing: border-box;
  border-radius: 4px;
  padding: 0 32px 0 48px;
}
.loading-icon {
  display: flex;
  justify-content: center;
}
</style>
